import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import type { ApiNameType } from '@/constants';
import { useSiteContext } from '@/providers';

import { authorizationHeader } from '../authorizationHeader.ts';
import { assertApiBaseForFetch } from './getApiUrl/getApiUrl.functions.ts';
import { useGetApiUrl } from './useGetApiUrl.ts';

type multiFetchProps = {
  baseApiRoute: ApiNameType;
  cacheKeys: string[];
  headers?: HeadersInit;
  routesArray: string[];
  refetchOnWindowFocus?: boolean;
  refetchInterval?: number;
  enabled?: boolean;
};

export const useAuthApiGetMultiFetch = <T>({
  baseApiRoute,
  cacheKeys,
  headers,
  routesArray,
  refetchOnWindowFocus = false,
  refetchInterval,
  enabled = true,
}: multiFetchProps) => {
  const auth = useAuth();
  const { currentSite } = useSiteContext();

  const api = useGetApiUrl(baseApiRoute);

  const getData = async (): Promise<T[]> => {
    const base = assertApiBaseForFetch(api);
    return await Promise.allSettled(
      routesArray.map(route =>
        fetch(`${base}${route}`, {
          headers: {
            ...authorizationHeader(auth.user),
            ...headers,
          },
        }).then(async response => {
          if (response.ok) {
            return response.json();
          }
          const errorText = await response.text();
          console.error(`Failed to fetch: `, errorText);

          if (response.status === 401) {
            throw new Error(`Unauthorized: Please log in to continue.`);
          }

          throw new Error(`Failed to fetch: ${response.statusText}`);
        })
      )
    ).then(r => {
      return r
        .filter((v: PromiseSettledResult<Awaited<T>>) => v.status === 'fulfilled')
        .map((v: PromiseFulfilledResult<T>) => v.value)
        .flat() as T[];
    });
  };
  return useQuery<T[]>({
    queryKey: [...cacheKeys, currentSite?.id],
    queryFn: getData,
    refetchOnWindowFocus,
    refetchInterval,
    enabled: enabled && api != null,
  });
};
